import { useEffect, useMemo, useState } from 'react';
import { useGamesStore } from '../../stores/gamesStore';
import {
  STATUS_LABELS,
  STATUS_STYLES,
  REJECTION_LABELS,
  formatDateTime,
  formatDuration,
  SearchIcon,
} from './shared';

// ============================================
// Вкладка «Партии»: журнал «кто, когда, во что и сколько»
// ============================================

const SummaryCards = ({ summary }) => {
  const cards = [
    { label: 'Партий', value: summary.runs.toLocaleString('ru-RU') },
    { label: 'Выдано звёзд', value: summary.stars.toLocaleString('ru-RU'), accent: true },
    { label: 'Игроков', value: summary.players.toLocaleString('ru-RU') },
    { label: 'Суммарный счёт', value: summary.score.toLocaleString('ru-RU') },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {cards.map((card) => (
        <div key={card.label} className="bg-bg-secondary rounded-xl border border-border-color p-4">
          <div className="text-xs text-text-secondary">{card.label}</div>
          <div className={`text-2xl font-bold mt-1 ${card.accent ? 'text-accent-primary' : 'text-text-primary'}`}>
            {card.value}
          </div>
        </div>
      ))}
    </div>
  );
};

const TopPlayers = ({ players }) => {
  if (!players || players.length === 0) return null;

  return (
    <div className="bg-bg-secondary rounded-xl border border-border-color p-4">
      <div className="text-xs text-text-secondary mb-3">Кто играл активнее всего</div>
      <div className="flex flex-wrap gap-2">
        {players.map((player) => (
          <div
            key={player.vk_user_id}
            className="flex items-center gap-2 px-3 py-1.5 bg-bg-tertiary rounded-lg text-sm"
          >
            <span className="font-mono text-text-primary">{player.vk_user_id}</span>
            <span className="text-text-secondary">{player.runs} пар.</span>
            <span className="text-accent-primary font-medium">{player.stars} ⭐</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const SessionsTab = ({ games }) => {
  const {
    sessions,
    summary,
    total,
    page,
    setPage,
    totalPages,
    isLoadingSessions,
    fetchSessions,
    fetchSummary,
  } = useGamesStore();

  const [search, setSearch] = useState('');
  const [gameId, setGameId] = useState('');
  const [status, setStatus] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const limit = 20;

  // Фильтры собираем в объект по значению: useEffect перезапускается
  // только когда реально изменился набор условий, а не каждый рендер.
  const filters = useMemo(
    () => ({ search, game_id: gameId, status, date_from: dateFrom, date_to: dateTo }),
    [search, gameId, status, dateFrom, dateTo]
  );

  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchSessions({ ...filters, limit, offset: (page - 1) * limit });
      fetchSummary(filters);
    }, 300);

    return () => clearTimeout(debounce);
  }, [filters, page]);

  const resetPage = (setter) => (value) => {
    setter(value);
    setPage(1);
  };

  return (
    <div className="space-y-6">
      <SummaryCards summary={summary} />

      <TopPlayers players={summary.topPlayers} />

      {/* Фильтры */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="relative">
          <input
            type="text"
            value={search}
            onChange={(e) => resetPage(setSearch)(e.target.value)}
            placeholder="Поиск по VK ID..."
            className="w-full pl-10 pr-4 py-2.5 bg-bg-secondary border border-border-color rounded-lg text-text-primary placeholder-text-muted focus:outline-none focus:border-accent-primary"
          />
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted">
            <SearchIcon />
          </div>
        </div>

        <select
          value={gameId}
          onChange={(e) => resetPage(setGameId)(e.target.value)}
          className="px-4 py-2.5 bg-bg-secondary border border-border-color rounded-lg text-text-primary focus:outline-none focus:border-accent-primary"
        >
          <option value="">Все игры</option>
          {games.map((game) => (
            <option key={game.game_id} value={game.game_id}>
              {game.title}
            </option>
          ))}
        </select>

        <select
          value={status}
          onChange={(e) => resetPage(setStatus)(e.target.value)}
          className="px-4 py-2.5 bg-bg-secondary border border-border-color rounded-lg text-text-primary focus:outline-none focus:border-accent-primary"
        >
          {Object.entries(STATUS_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>

        <div>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => resetPage(setDateFrom)(e.target.value)}
            title="С"
            className="w-full px-3 py-2.5 bg-bg-secondary border border-border-color rounded-lg text-text-primary focus:outline-none focus:border-accent-primary"
          />
        </div>

        <div>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => resetPage(setDateTo)(e.target.value)}
            title="По"
            className="w-full px-3 py-2.5 bg-bg-secondary border border-border-color rounded-lg text-text-primary focus:outline-none focus:border-accent-primary"
          />
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div className="text-sm text-text-secondary">
          Всего: <span className="text-text-primary font-medium">{total}</span>
        </div>
        {(search || gameId || status !== 'all' || dateFrom || dateTo) && (
          <button
            onClick={() => {
              setSearch('');
              setGameId('');
              setStatus('all');
              setDateFrom('');
              setDateTo('');
              setPage(1);
            }}
            className="text-sm text-text-secondary hover:text-text-primary transition-colors"
          >
            Сбросить фильтры
          </button>
        )}
      </div>

      {/* Таблица */}
      <div className="bg-bg-secondary rounded-xl border border-border-color overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-bg-tertiary border-b border-border-color">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">ID</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">Начало</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">Конец</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">VK User ID</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">Игра</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-text-secondary uppercase tracking-wider">Счёт</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-text-secondary uppercase tracking-wider">Звёзды</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">Статус</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border-color">
              {isLoadingSessions ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-text-secondary">
                    <div className="animate-spin h-6 w-6 border-2 border-accent-primary border-t-transparent rounded-full mx-auto" />
                  </td>
                </tr>
              ) : sessions.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-text-secondary">
                    Партий не найдено
                  </td>
                </tr>
              ) : (
                sessions.map((session) => {
                  const duration = formatDuration(session.started_at, session.finished_at);

                  return (
                  <tr key={session.id} className="hover:bg-bg-tertiary/50 transition-colors">
                    <td className="px-4 py-3 text-sm text-text-muted font-mono">{session.id}</td>
                    <td className="px-4 py-3 text-sm text-text-secondary whitespace-nowrap">
                      {formatDateTime(session.started_at)}
                    </td>
                    <td className="px-4 py-3 text-sm text-text-secondary whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <span>{formatDateTime(session.finished_at)}</span>
                        {duration ? (
                          <span className="px-2 py-0.5 rounded text-xs bg-bg-tertiary text-text-secondary">
                            {duration}
                          </span>
                        ) : null}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-text-secondary font-mono">{session.vk_user_id}</td>
                    <td className="px-4 py-3 text-sm text-text-primary">
                      {session.game_title || session.game_id}
                      {session.is_new_record ? (
                        <span className="ml-2 px-2 py-0.5 rounded text-xs bg-amber-500/10 text-amber-400">
                          рекорд
                        </span>
                      ) : null}
                    </td>
                    <td className="px-4 py-3 text-sm text-text-primary text-right">
                      {session.score === null ? '—' : session.score}
                    </td>
                    <td className="px-4 py-3 text-sm text-right">
                      <span className={session.stars_awarded > 0 ? 'text-accent-primary font-medium' : 'text-text-muted'}>
                        {session.stars_awarded > 0 ? `+${session.stars_awarded}` : '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${STATUS_STYLES[session.status] || 'bg-text-muted/10 text-text-secondary'}`}>
                        {STATUS_LABELS[session.status] || session.status}
                      </span>
                      {session.rejection_code ? (
                        <div className="text-xs text-text-muted mt-1">
                          {REJECTION_LABELS[session.rejection_code] || session.rejection_code}
                        </div>
                      ) : null}
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-text-secondary">
            Страница {page} из {totalPages}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1 || isLoadingSessions}
              className="px-4 py-2 bg-bg-secondary border border-border-color rounded-lg text-text-primary disabled:opacity-50 disabled:cursor-not-allowed hover:bg-bg-tertiary transition-colors"
            >
              Назад
            </button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages || isLoadingSessions}
              className="px-4 py-2 bg-bg-secondary border border-border-color rounded-lg text-text-primary disabled:opacity-50 disabled:cursor-not-allowed hover:bg-bg-tertiary transition-colors"
            >
              Вперёд
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export { SessionsTab };
